import { inject, Injectable, NgZone } from '@angular/core';
import { HubConnection, HubConnectionBuilder, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';
import { UserSessionService } from '@nha/auth';
import { SignalRRetryPolicy } from './signalr-retry.policy';

const FALLBACK_DELAY_MS = 60_000;

interface IServerShutdownPayload {
	retryAfterMs: number;
}

/**
 * Thin base class for SignalR hub services. Owns the connection lifecycle (build, start
 * with retry, listen for ServerShutdown, disconnect) and provides a zone-wrapping helper
 * for server-pushed callbacks.
 *
 * Subclasses control:
 *  - `hubUrl` — the endpoint to connect to
 *  - `registerHandlers(conn)` — server method names to listen for and how to react
 *  - WHEN to call `connect()` / `disconnect()` (auto on token, lazy on demand, etc.)
 *
 * Subclasses do NOT need to handle the "already connecting" race — concurrent `connect()`
 * calls share a single in-flight start.
 */
@Injectable()
export abstract class SignalRHubBase {
	private _connecting: Promise<void> | null = null;

	protected session = inject(UserSessionService);
	protected retryPolicy = inject(SignalRRetryPolicy);
	protected zone = inject(NgZone);
	protected connection: HubConnection | null = null;

	protected abstract get hubUrl(): string;
	protected abstract registerHandlers(conn: HubConnection): void;

	/**
	 * Options passed to `withUrl`. Defaults to the negotiate flow with the bearer token from
	 * the auth session — which lets SignalR fall back to SSE/long-polling but requires LB
	 * session affinity across multiple instances. Subclasses can override to pin a transport
	 * (e.g. WebSockets-only) when they can guarantee the upgrade path and want affinity-free
	 * connections. Spread `super.connectionOptions` to keep the access token.
	 */
	protected get connectionOptions(): IHttpConnectionOptions {
		return { accessTokenFactory: () => this.session.getTokenFromCookie() };
	}

	protected async connect(): Promise<void> {
		if (this.connection) {
			return;
		}
		if (this._connecting !== null) {
			return this._connecting;
		}

		this._connecting = (async () => {
			const conn = this._buildConnection();
			this.registerHandlers(conn);
			try {
				await this._startWithRetry(conn);
				this.connection = conn;
			} catch (err) {
				console.error(`${this.constructor.name} failed to start:`, err);
			}
		})();

		try {
			await this._connecting;
		} finally {
			this._connecting = null;
		}
	}

	protected async disconnect(): Promise<void> {
		if (!this.connection) {
			return;
		}
		const conn = this.connection;
		this.connection = null;
		try {
			await conn.stop();
		} catch (err) {
			console.warn(`${this.constructor.name} disconnect error:`, err);
		}
	}

	/**
	 * Wrap a callback so SignalR-invoked handlers re-enter Angular's zone. SignalR fires
	 * callbacks on a microtask outside the zone, which means signal/observable updates
	 * inside a raw handler do not trigger change detection.
	 */
	protected zoned<T extends unknown[]>(fn: (...args: T) => void): (...args: T) => void {
		return (...args: T) => this.zone.run(() => fn(...args));
	}

	/**
	 * Builds the HubConnection with hardened defaults: token from the auth session, the
	 * shared SignalRRetryPolicy for transport-loss reconnects, and a ServerShutdown handler
	 * that captures the retry-after hint for the next reconnect.
	 *
	 * accessTokenFactory must return the raw token (no "Bearer " prefix). The JS client
	 * adds the prefix automatically.
	 */
	private _buildConnection(): HubConnection {
		const conn = new HubConnectionBuilder()
			.withUrl(this.hubUrl, this.connectionOptions)
			.withAutomaticReconnect(this.retryPolicy)
			.configureLogging(LogLevel.Warning)
			.build();

		/** capture the server's drain hint so the next reconnect honors it */
		conn.on('ServerShutdown', (payload: IServerShutdownPayload) => {
			this.retryPolicy.captureShutdownHint(payload.retryAfterMs);
		});

		return conn;
	}

	/**
	 * Wraps connection.start() in the same retry policy used for transport-loss reconnects.
	 * withAutomaticReconnect() does NOT retry initial-start failures — that's what
	 * negotiate-503 produces during server drain. Without this, a client opening a tab while
	 * the backend is recycling would never connect.
	 */
	private async _startWithRetry(conn: HubConnection): Promise<void> {
		let attempt = 0;
		let started = false;
		while (!started) {
			try {
				await conn.start();
				started = true;
			} catch (error) {
				const delay =
					this.retryPolicy.nextRetryDelayInMilliseconds({
						previousRetryCount: attempt,
						elapsedMilliseconds: 0,
						retryReason: error as Error
					}) ?? FALLBACK_DELAY_MS;
				/**
				 * The retry loop never gives up (the policy never returns null), which is the
				 * desired resilience: when connectivity returns, the pending connect() resolves
				 * and queued subscriptions fire. But a client that can NEVER connect (e.g. a
				 * WebSockets-only hub behind a network that blocks the WS upgrade) would otherwise
				 * retry forever in total silence. Warn each attempt so a permanently-stuck client
				 * is visible in logs/telemetry instead of failing invisibly.
				 */
				console.warn(`${this.constructor.name} start attempt ${attempt + 1} failed, retrying in ${Math.round(delay)}ms:`, error);
				await new Promise<void>((resolve) => setTimeout(resolve, delay));
				attempt++;
			}
		}
	}
}
